import React, { useState, useEffect } from 'react';
import { tenantDoctorAPI } from '../../../services/api/tenantDoctorAPI';
import { useAuth } from '../../../services/contexts/AuthContext';
import toast from 'react-hot-toast';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  UserCheck,
  Activity, 
  Clock, 
  CheckCircle,
  AlertTriangle,
  Calendar,
  Filter,
  Download,
  RefreshCw,
  Eye,
  Heart,
  Brain,
  Award,
  Target,
  Zap,
  FileText
} from 'lucide-react';

const ClinicalAnalytics = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response = await tenantDoctorAPI.getDashboardSummary();
      
      // Mock analytics data for demonstration
      const mockAnalyticsData = {
        totalPatients: 156,
        reviewsCompleted: 89,
        pendingReviews: 12,
        criticalAlerts: 3,
        avgReviewTime: 4.2,
        accuracyRate: 98.7,
        patientCount: 156,
        vitalsCount: 234,
        userCounts: [
          { role: 'tenant_doctor', count: 5 },
          { role: 'tenant_nurse', count: 12 }
        ],
        monthlyTrends: [
          { month: 'Jan', reviews: 45, patients: 23 },
          { month: 'Feb', reviews: 52, patients: 28 },
          { month: 'Mar', reviews: 48, patients: 25 },
          { month: 'Apr', reviews: 61, patients: 32 },
          { month: 'May', reviews: 55, patients: 29 },
          { month: 'Jun', reviews: 67, patients: 35 }
        ],
        patientDemographics: [
          { age: '18-30', count: 25, percentage: 16 },
          { age: '31-45', count: 42, percentage: 27 },
          { age: '46-60', count: 38, percentage: 24 },
          { age: '60+', count: 51, percentage: 33 }
        ],
        clinicalMetrics: [
          { metric: 'Heart Rate Variability', avg: 45.2, trend: '+2.1%' },
          { metric: 'Blood Pressure', avg: 128.5, trend: '-1.3%' },
          { metric: 'Respiratory Rate', avg: 16.8, trend: '+0.5%' },
          { metric: 'Oxygen Saturation', avg: 98.2, trend: '+0.2%' }
        ]
      };
      
      setAnalytics(mockAnalyticsData);
    } catch (error) {
      console.error('Failed to load analytics data:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const MetricCard = ({ title, value, icon: Icon, color = "vital-blue" }) => {
    const colorClasses = {
      'vital-blue': 'bg-[#DCEAF5] text-[#207CE5]',
      'cyan-core': 'bg-[#DCEAF5] text-[#00BCD4]',
      'teal-signal': 'bg-[#DCEAF5] text-[#008080]',
      'steel-blue': 'bg-[#DCEAF5] text-[#4682B4]'
    };
    
    const bgClass = colorClasses[color] || colorClasses['vital-blue'];
    
    return (
      <div className="bg-[#F9FBFC] p-6 rounded-xl shadow-sm border border-[#DCEAF5]">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-[#3D3D3D]">{title}</p>
            <p className="text-2xl font-bold text-[#0B1C2C] mt-2">{value}</p>
          </div>
          <div className={`p-3 rounded-lg ${bgClass.split(' ')[0]}`}>
            <Icon className={`h-6 w-6 ${bgClass.split(' ')[1]}`} />
          </div>
        </div>
      </div>
    );
  };

  const UserRoleChart = () => {
    if (!analytics?.userCounts) return null;

    const roleColors = {
      'tenant_doctor': 'bg-[#207CE5]',
      'tenant_nurse': 'bg-[#008080]', 
      'tenant_admin': 'bg-[#3D3D3D]',
      'tenant_technician': 'bg-[#00BCD4]'
    };

    const roleDisplayNames = {
      'tenant_doctor': 'Clinician',
      'tenant_nurse': 'Nurse',
      'tenant_admin': 'Admin',
      'tenant_technician': 'Technician'
    };

    const total = analytics.userCounts.reduce((sum, item) => sum + item.count, 0);

    return (
      <div className="bg-[#F9FBFC] p-6 rounded-xl shadow-sm border border-[#DCEAF5]">
        <h3 className="text-lg font-semibold text-[#0B1C2C] mb-4">Patient Distribution by Role</h3>
        
        <div className="space-y-4">
          {analytics.userCounts.map((item) => {
            const percentage = total > 0 ? (item.count / total * 100) : 0;
            const roleName = roleDisplayNames[item.role] || item.role.replace('tenant_', '').charAt(0).toUpperCase() + 
                            item.role.replace('tenant_', '').slice(1);
            
            return (
              <div key={item.role} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded ${roleColors[item.role] || 'bg-[#3D3D3D]'}`}></div>
                  <span className="text-sm font-medium text-[#3D3D3D]">{roleName}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-24 bg-[#DCEAF5] rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${roleColors[item.role] || 'bg-[#3D3D3D]'}`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-sm text-[#3D3D3D] w-12 text-right">
                    {item.count}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {analytics.userCounts.length === 0 && (
          <div className="text-center py-8">
            <Users className="h-8 w-8 text-[#3D3D3D] mx-auto mb-2" />
            <p className="text-sm text-[#3D3D3D]">No patient data available</p>
          </div>
        )}
      </div>
    );
  };

  const ActivityChart = () => (
    <div className="bg-[#F9FBFC] p-6 rounded-xl shadow-sm border border-[#DCEAF5]">
      <h3 className="text-lg font-semibold text-[#0B1C2C] mb-4">Clinical Activity Trends</h3>
      
      <div className="h-64 flex items-center justify-center bg-[#DCEAF5] rounded-lg">
        <div className="text-center">
          <BarChart3 className="h-12 w-12 text-[#3D3D3D] mx-auto mb-2" />
          <p className="text-sm text-[#3D3D3D]">Clinical activity chart coming soon</p>
          <p className="text-xs text-[#3D3D3D]">Historical clinical data will be displayed here</p>
        </div>
      </div>
    </div>
  );

  const RecentActivity = () => (
    <div className="bg-[#F9FBFC] p-6 rounded-xl shadow-sm border border-[#DCEAF5]">
      <h3 className="text-lg font-semibold text-[#0B1C2C] mb-4">Recent Clinical Activity</h3>
      
      <div className="space-y-4">
        {[
          { action: 'Patient review completed', time: '2 hours ago', icon: CheckCircle, color: 'text-[#008080]' },
          { action: 'Vital signs recorded', time: '4 hours ago', icon: Activity, color: 'text-[#207CE5]' },
          { action: 'Clinical notes added', time: '6 hours ago', icon: FileText, color: 'text-[#00BCD4]' },
          { action: 'Patient assessment updated', time: '8 hours ago', icon: UserCheck, color: 'text-[#4682B4]' },
        ].map((activity, index) => (
          <div key={index} className="flex items-center space-x-3 p-3 rounded-lg bg-[#DCEAF5]">
            <activity.icon className={`h-5 w-5 ${activity.color}`} />
            <div className="flex-1">
              <p className="text-sm font-medium text-[#0B1C2C]">{activity.action}</p>
              <p className="text-xs text-[#3D3D3D]">{activity.time}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#207CE5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0B1C2C]">Clinical Analytics</h1>
          <p className="text-[#3D3D3D]">Monitor your clinical performance and patient metrics</p>
        </div>
        <div className="flex space-x-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
          <button
            onClick={loadAnalytics}
            className="flex items-center space-x-2 px-4 py-2 border border-[#DCEAF5] rounded-lg hover:bg-[#DCEAF5]"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
          <button className="flex items-center space-x-2 px-4 py-2 bg-[#207CE5] text-white rounded-lg hover:bg-[#4682B4]">
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Patients"
          value={analytics?.totalPatients || 0}
          icon={Users}
          color="vital-blue"
        />
        <MetricCard
          title="Reviews Completed"
          value={analytics?.reviewsCompleted || 0}
          icon={CheckCircle}
          color="teal-signal"
        />
        <MetricCard
          title="Pending Reviews"
          value={analytics?.pendingReviews || 0}
          icon={Clock}
          color="cyan-core"
        />
        <MetricCard
          title="Critical Alerts"
          value={analytics?.criticalAlerts || 0}
          icon={AlertTriangle}
          color="steel-blue"
        />
      </div>

      {/* Charts and Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UserRoleChart />
        <ActivityChart />
      </div>

      {/* Recent Activity and Additional Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentActivity />
        
        {/* Performance Insights */}
        <div className="bg-[#F9FBFC] p-6 rounded-xl shadow-sm border border-[#DCEAF5]">
          <h3 className="text-lg font-semibold text-[#0B1C2C] mb-4">Clinical Performance Insights</h3>
          
          <div className="space-y-4">
            <div className="p-4 bg-[#DCEAF5] border border-[#008080] rounded-lg">
              <div className="flex items-center space-x-3">
                <TrendingUp className="h-5 w-5 text-[#008080]" />
                <div>
                  <p className="text-sm font-medium text-[#0B1C2C]">High Review Accuracy</p>
                  <p className="text-xs text-[#008080]">High accuracy rate in clinical reviews</p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-[#DCEAF5] border border-[#207CE5] rounded-lg">
              <div className="flex items-center space-x-3">
                <Activity className="h-5 w-5 text-[#207CE5]" />
                <div>
                  <p className="text-sm font-medium text-[#0B1C2C]">Efficient Review Process</p>
                  <p className="text-xs text-[#207CE5]">Efficient review process</p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-[#DCEAF5] border border-[#00BCD4] rounded-lg">
              <div className="flex items-center space-x-3">
                <Users className="h-5 w-5 text-[#00BCD4]" />
                <div>
                  <p className="text-sm font-medium text-[#0B1C2C]">Patient Engagement</p>
                  <p className="text-xs text-[#00BCD4]">Active patients under care</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Reports Section */}
      <div className="bg-[#F9FBFC] p-6 rounded-xl shadow-sm border border-[#DCEAF5]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#0B1C2C]">Clinical Reports</h3>
          <button className="flex items-center space-x-2 px-4 py-2 border border-[#DCEAF5] rounded-lg hover:bg-[#DCEAF5]">
            <Filter className="h-4 w-4" />
            <span>Filter Reports</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 border border-[#DCEAF5] rounded-lg hover:border-[#207CE5] cursor-pointer transition-colors">
            <h4 className="font-medium text-[#0B1C2C] mb-2">Patient Health Summary</h4>
            <p className="text-sm text-[#3D3D3D] mb-3">Comprehensive overview of patient health metrics and trends</p>
            <button className="text-sm text-[#207CE5] hover:text-[#4682B4] font-medium">
              Generate Report →
            </button>
          </div>

          <div className="p-4 border border-[#DCEAF5] rounded-lg hover:border-[#207CE5] cursor-pointer transition-colors">
            <h4 className="font-medium text-[#0B1C2C] mb-2">Clinical Review Report</h4>
            <p className="text-sm text-[#3D3D3D] mb-3">Detailed analysis of clinical reviews and outcomes</p>
            <button className="text-sm text-[#207CE5] hover:text-[#4682B4] font-medium">
              Generate Report →
            </button>
          </div>

          <div className="p-4 border border-[#DCEAF5] rounded-lg hover:border-[#207CE5] cursor-pointer transition-colors">
            <h4 className="font-medium text-[#0B1C2C] mb-2">Vital Signs Analysis</h4>
            <p className="text-sm text-[#3D3D3D] mb-3">Trends and patterns in patient vital signs data</p>
            <button className="text-sm text-[#207CE5] hover:text-[#4682B4] font-medium">
              Generate Report →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicalAnalytics; 